import { defineStore } from 'pinia';
import { computed, ref, toRaw, watch } from 'vue';
import { http } from '../services/http';
import {
    CustomDataValuePair,
    EchoFilter,
    EchoFilterCheckbox,
    EchoFilterCustomRequestData,
    EchoFilterRequest,
    EchoLevelFilterCheckbox,
    EchoSelectedFilter,
    FilterStoreName,
    LevelFilterRequest,
    RelativeDateRangeDto,
} from '../services/api/filter/types';
import dayjs from 'dayjs';
import { defaultDateRange } from '../utils/dateUtils';
import { useKeycloak } from '../composables/useKeycloak';

const pluckSelectedCheckboxes = (
    items: ReadonlyArray<EchoFilterCheckbox>,
    key: 'id' | 'name' = 'id'
): any[] => {
    return flattenedCheckboxes(items)
        .filter((item) => item.isSelected)
        .map((item) => item[key]);
};

/**
 * A session restored from before value ids shipped still carries the type's guid as each custom
 * checkbox id. Those items travel in selectedValues only, until the next filter response replaces them.
 */
const pluckSelectedCustomDataValuePairs = (
    items: ReadonlyArray<EchoFilterCheckbox>
): CustomDataValuePair[] =>
    flattenedCheckboxes(items).flatMap((item) =>
        item.isSelected && typeof item.id === 'number' ? [{ id: item.id, name: item.name }] : []
    );

// Level filter only: a selected group means the whole team, so we save the group and skip the
// agents it already covers. The backend expands a saved group to the team's current members,
// which keeps agents added after the filter was saved included.
// Every selected group in the subtree is saved, leaf subgroups included, not just the topmost. The
// backend matches an agent's direct (leaf) group, so a nested team needs its leaf subgroup ids
// present; saving only an ancestor id would filter its members out.
const pluckSelectedLevelFilter = (items: EchoLevelFilterCheckbox[]): LevelFilterRequest => {
    const levelFilter: LevelFilterRequest = { groupIds: [], agents: [] };

    const traverse = (item: EchoLevelFilterCheckbox, ancestorSelected: boolean) => {
        // A group without members has no children, so tell groups and agents apart by agentId.
        if (!item.agentId) {
            if (item.isSelected) {
                levelFilter.groupIds.push(item.groupId);
            }
            item.children.forEach((child) => traverse(child, ancestorSelected || item.isSelected));
        } else if (item.isSelected && !ancestorSelected) {
            levelFilter.agents.push({ groupId: item.groupId, agentId: item.agentId });
        }
    };

    items.forEach((item) => traverse(item, false));

    return levelFilter;
};

const hasAnySelected = (items: ReadonlyArray<EchoFilterCheckbox>): boolean =>
    items.some((item) => item.isSelected || hasAnySelected(item.children));

const flattenedCheckboxes = (items: ReadonlyArray<EchoFilterCheckbox>): EchoFilterCheckbox[] => {
    // Skip the clone-and-traverse below when nothing here is selected: every caller filters
    // the result down to selected items anyway, so it would come back empty regardless.
    if (!hasAnySelected(items)) {
        return [];
    }

    const flattened: EchoFilterCheckbox[] = [];

    function traverse(item: EchoFilterCheckbox) {
        const allChildrenSelected =
            item.children.length > 0 && item.children.every((c) => c.isSelected);

        flattened.push({
            ...item,
            allChildrenSelected,
        });

        item.children.forEach((child) => (child.parentFullySelected = allChildrenSelected));

        item.children.forEach(traverse);
    }

    items.forEach(traverse);

    return flattened;
};

const flattenedAndSelectedCheckboxes = (items?: EchoFilterCheckbox[]): EchoFilterCheckbox[] => {
    if (!items) {
        return [];
    }

    return (
        (flattenedCheckboxes(items).filter((item) => {
            const selectedItem = !item.isGroup && item.isSelected;

            const selectedGroupWithAllChildrenSelected =
                item.isGroup && item.isSelected && item.allChildrenSelected;

            return (
                (selectedItem && !item.parentFullySelected) ||
                (selectedGroupWithAllChildrenSelected && !item.parentFullySelected)
            );
        }) as EchoFilterCheckbox[]) || []
    );
};

type GetFilterOptions = { endpoint?: 'filter' | 'protocols/filter' };

const filterStore = (name: FilterStoreName) => {
    const all = ref<EchoFilter>(null);
    const onlyResolvedBothCheckedAsNull = ref(false);
    const recordingStartLabel = ref<string | null>(null);
    const recordingEndLabel = ref<string | null>(null);
    const selectedFavoriteFilter = ref<null | { id: string; name: string }>(null);
    const selectedFavoriteFilterRequestData = ref<string | null>(null);
    const filterStoreName = name;
    const loading = ref(false);
    let getFilterAbortController: AbortController | null = null;
    let applyFilterAbortController: AbortController | null = null;
    let updateFilterAbortController: AbortController | null = null;

    const requestData = computed(() => {
        const filter = {} as EchoFilterRequest;

        const defaultRange = defaultDateRange();

        const hasRelativeDateRange = !!all.value?.relativeDateRange;

        filter.relativeDateRange = all.value?.relativeDateRange ?? null;

        // IMPORTANT: widgets are rule-only. If relativeDateRange exists, do not send start/end.
        filter.startTime = hasRelativeDateRange
            ? null
            : all.value?.startTime
              ? dayjs(all.value.startTime).format('YYYY-MM-DD')
              : defaultRange.start;

        filter.endTime = hasRelativeDateRange
            ? null
            : all.value?.endTime
              ? dayjs(all.value.endTime).format('YYYY-MM-DD')
              : defaultRange.end;
        filter.keywordFilter = {
            type: all.value?.keywordFilter?.selectedWordSearchType || 'And',
            words: Array.from(all.value?.keywordFilter?.words || []),
            excludedWords: Array.from(all.value?.keywordFilter?.excludedWords || []),
        };
        filter.trackerFilter = {
            type: all.value?.trackerFilter?.selectedGroupType || 'All',
            selectedIds: pluckSelectedCheckboxes(all.value?.trackerFilter?.filter || []),
        };
        filter.levelFilter = pluckSelectedLevelFilter(all.value?.levelFilter || []);
        if (all.value?.levelFilterMatchesNothing) {
            filter.levelFilter.matchesNothing = true;
        }
        filter.skillFilter = pluckSelectedCheckboxes(all.value?.skillFilter || []);
        filter.channelFilter = pluckSelectedCheckboxes(all.value?.channelFilter || []);
        filter.categoryFilter = pluckSelectedCheckboxes(all.value?.categoryFilter || []);
        filter.sentimentAgentFilter = pluckSelectedCheckboxes(
            all.value?.sentimentAgentFilter || []
        );
        filter.sentimentAgentDirectionFilter = pluckSelectedCheckboxes(
            all.value?.sentimentAgentDirectionFilter || []
        );
        filter.sentimentCustomerFilter = pluckSelectedCheckboxes(
            all.value?.sentimentCustomerFilter || []
        );
        filter.sentimentCustomerDirectionFilter = pluckSelectedCheckboxes(
            all.value?.sentimentCustomerDirectionFilter || []
        );
        filter.protocolFormDefinitionFilter = pluckSelectedCheckboxes(
            all.value?.protocolFormDefinitionFilter || []
        );
        filter.submittedByUserFilter = pluckSelectedCheckboxes(
            all.value?.submittedByUserFilter || []
        );
        filter.protocolAgentFilter = pluckSelectedCheckboxes(all.value?.protocolAgentFilter || []);
        filter.protocolGradeFilter = pluckSelectedCheckboxes(all.value?.protocolGradeFilter || []);
        filter.isNew = all.value?.isNew || null;
        filter.submittedByMe = all.value?.submittedByMe ?? null;
        filter.createdByAi = all.value?.createdByAi ?? null;
        filter.editedByHuman = all.value?.editedByHuman ?? null;
        filter.pendingApproval = all.value?.pendingApproval ?? null;
        filter.actionRequired = all.value?.actionRequired ?? null;
        filter.queueFilter = pluckSelectedCheckboxes(all.value?.queueFilter || []);
        filter.customDataFilter =
            all.value?.customDataFilter?.map((item) => {
                const filter: EchoFilterCustomRequestData = {
                    key: item.key,
                    selectedValuePairs: [],
                    words: item.words ?? [],
                };

                if (item.filter) {
                    filter.selectedValuePairs = pluckSelectedCustomDataValuePairs(item.filter);
                }

                if (item.words) {
                    filter.words = [...item.words];
                }

                return filter;
            }) || [];
        filter.recordingStart = all.value?.recordingStart || null;
        filter.recordingEnd = all.value?.recordingEnd || null;

        filter.onlyResolved =
            all.value?.onlyResolved === true
                ? true
                : all.value?.onlyResolved === false
                  ? false
                  : null;

        filter.hasTranscriptions = all.value?.hasTranscriptions ?? true;

        return filter;
    });

    // Comparison key for favorite-filter selection, excluding the date span: changing only the
    // date range must not deselect an active favorite (favorites carry no date span).
    const favoriteComparisonKey = computed(() => {
        const { startTime, endTime, relativeDateRange, ...comparable } = requestData.value;
        return JSON.stringify(comparable);
    });

    // Set when the user removes level selections that no longer exist, until a new level is selected.
    const levelFilterWasReset = ref(false);

    // A new selection in the level tree replaces the saved selections that no longer exist.
    watch(
        () => {
            const levelFilter = requestData.value.levelFilter;
            return !!levelFilter?.groupIds.length || !!levelFilter?.agents.length;
        },
        (hasLevelSelection) => {
            if (!hasLevelSelection) return;
            if (all.value?.levelFilterMatchesNothing) {
                all.value.levelFilterMatchesNothing = false;
            }
            levelFilterWasReset.value = false;
        }
    );

    const removeInvalidLevelFilter = () => {
        if (!all.value) return;
        all.value.levelFilterMatchesNothing = false;
        levelFilterWasReset.value = true;
    };

    const selected = computed((): EchoSelectedFilter => {
        if (!all.value) {
            return {} as EchoSelectedFilter;
        }

        const filters: EchoSelectedFilter = {
            startTime: all.value.startTime,
            endTime: all.value.endTime,
            relativeDateRange: all.value.relativeDateRange ?? null,
            keywordFilter: {
                words: all.value.keywordFilter?.words || [],
                excludedWords: all.value.keywordFilter?.excludedWords || [],
                selectedWordSearchType: all.value.keywordFilter?.selectedWordSearchType || 'And',
                availableWordSearchTypes: all.value.keywordFilter?.availableWordSearchTypes || [
                    'And',
                    'Or',
                ],
            },
            trackerFilter: {
                selectedGroupType: all.value.trackerFilter?.selectedGroupType || 'All',
                filter: flattenedAndSelectedCheckboxes(all.value.trackerFilter?.filter),
            },
            levelFilter: flattenedAndSelectedCheckboxes(all.value.levelFilter),
            skillFilter: flattenedAndSelectedCheckboxes(all.value.skillFilter),
            channelFilter: flattenedAndSelectedCheckboxes(all.value.channelFilter),
            categoryFilter: flattenedAndSelectedCheckboxes(all.value.categoryFilter),
            sentimentAgentFilter: flattenedAndSelectedCheckboxes(all.value.sentimentAgentFilter),
            sentimentAgentDirectionFilter: flattenedAndSelectedCheckboxes(
                all.value.sentimentAgentDirectionFilter
            ),
            sentimentCustomerFilter: flattenedAndSelectedCheckboxes(
                all.value.sentimentCustomerFilter
            ),
            sentimentCustomerDirectionFilter: flattenedAndSelectedCheckboxes(
                all.value.sentimentCustomerDirectionFilter
            ),
            protocolFormDefinitionFilter: flattenedAndSelectedCheckboxes(
                all.value.protocolFormDefinitionFilter
            ),
            submittedByUserFilter: flattenedAndSelectedCheckboxes(all.value.submittedByUserFilter),
            protocolAgentFilter: flattenedAndSelectedCheckboxes(all.value.protocolAgentFilter),
            protocolGradeFilter: flattenedAndSelectedCheckboxes(all.value.protocolGradeFilter),
            isNew: all.value.isNew,
            submittedByMe: all.value.submittedByMe,
            createdByAi: all.value.createdByAi,
            editedByHuman: all.value.editedByHuman,
            pendingApproval: all.value.pendingApproval,
            actionRequired: all.value.actionRequired,
            queueFilter: flattenedAndSelectedCheckboxes(all.value.queueFilter),
            customDataFilter: (all.value.customDataFilter ?? []).map((x) => {
                return {
                    ...x,
                    filter: (x.filter ?? []).filter((x) => x.isSelected),
                };
            }),
            recordingStart: all.value.recordingStart || null,
            recordingEnd: all.value.recordingEnd || null,
            onlyResolved:
                all.value.onlyResolved === true
                    ? true
                    : all.value.onlyResolved === false
                      ? false
                      : null,
            hasTranscriptions: all.value.hasTranscriptions,
        };

        return filters;
    });

    const setFilter = (filter: EchoFilter) => {
        if (filter) {
            if (filter.startTime) filter.startTime = dayjs(filter.startTime).format('YYYY-MM-DD');
            if (filter.endTime) filter.endTime = dayjs(filter.endTime).format('YYYY-MM-DD');

            // If the backend response omits relativeDateRange (undefined), preserve the current
            // value so a user-set relative date range survives a getFilter() round-trip.
            // If the backend explicitly returns null, that takes precedence.
            filter.relativeDateRange =
                filter.relativeDateRange !== undefined
                    ? filter.relativeDateRange
                    : (all.value?.relativeDateRange ?? null);
        }

        all.value = filter;
    };

    const loadFilter = (filter: EchoFilter) => {
        setFilter(filter);
        levelFilterWasReset.value = false;
    };

    const resetFilter = (options?: GetFilterOptions) => {
        all.value = null;
        getFilter(options);
        onlyResolvedBothCheckedAsNull.value = false;
        selectedFavoriteFilter.value = null;
        levelFilterWasReset.value = false;
    };

    const getFilter = (options?: GetFilterOptions) => {
        getFilterAbortController?.abort();
        getFilterAbortController = new AbortController();
        loading.value = true;
        const signal = getFilterAbortController.signal;
        const endpoint = options?.endpoint ?? 'filter';

        return http
            .post(endpoint, { ...requestData.value }, { signal })
            .then((response) => {
                if (!signal.aborted) {
                    setFilter(response.data.filter);
                }
            })
            .catch((error) => {
                if (!signal.aborted) throw error;
            })
            .finally(() => {
                if (!signal.aborted) loading.value = false;
            });
    };

    const applyFilterRequest = (filterReq: EchoFilterRequest) => {
        applyFilterAbortController?.abort();
        applyFilterAbortController = new AbortController();
        const signal = applyFilterAbortController.signal;

        // A favorite filter carries no date span, so loading one keeps the user's currently-active
        // range. Capture it from requestData before setFilter overwrites the current filter.
        const requestWithCurrentDateSpan: EchoFilterRequest = {
            ...filterReq,
            startTime: requestData.value.startTime,
            endTime: requestData.value.endTime,
            relativeDateRange: requestData.value.relativeDateRange,
        };

        return http
            .post('filter', requestWithCurrentDateSpan, { signal })
            .then((response) => {
                if (!signal.aborted) {
                    loadFilter(response.data.filter);
                    selectedFavoriteFilterRequestData.value = favoriteComparisonKey.value;
                }
            })
            .catch((error) => {
                if (!signal.aborted) throw error;
            });
    };

    const updateFilter = (chatId: string) => {
        updateFilterAbortController?.abort();
        updateFilterAbortController = new AbortController();
        loading.value = true;
        const signal = updateFilterAbortController.signal;

        return http
            .patch(`chat/${chatId}/update-filter`, requestData.value, { signal })
            .then((response) => {
                if (!signal.aborted) {
                    setFilter(response.data.filter);
                }
            })
            .catch((error) => {
                if (!signal.aborted) throw error;
            })
            .finally(() => {
                if (!signal.aborted) loading.value = false;
            });
    };

    const setIncludedWords = (words: string[]) => {
        if (all.value?.keywordFilter) {
            all.value.keywordFilter.words = [...words];
        }
    };

    const setExcludedWords = (words: string[]) => {
        if (all.value?.keywordFilter) {
            all.value.keywordFilter.excludedWords = [...words];
        }
    };

    const setStartEndTime = (startTime: string, endTime: string) => {
        if (all.value) {
            all.value.startTime = dayjs(startTime).format('YYYY-MM-DD');
            all.value.endTime = dayjs(endTime).format('YYYY-MM-DD');

            all.value.relativeDateRange = null;
        }
    };

    const setRelativeDateRange = (dto: RelativeDateRangeDto | null) => {
        if (!all.value) return;

        all.value.relativeDateRange = dto;
        // requestData excludes startTime/endTime when relativeDateRange is set,
        // so we don't clear them here — clearing triggers a feedback loop through DateRangeFilter
    };

    const clearAbsoluteDates = () => {
        if (!all.value) return;
        // backend relies purely on relative rule
        all.value.startTime = '';
        all.value.endTime = '';
    };

    const setSelectedFavoriteFilter = (fav: { id: string; name: string } | null) => {
        selectedFavoriteFilter.value = fav;
        if (fav) {
            selectedFavoriteFilterRequestData.value = favoriteComparisonKey.value;
        }
    };

    const clearSelectedFavoriteFilter = () => {
        selectedFavoriteFilter.value = null;
        selectedFavoriteFilterRequestData.value = null;
    };

    const seedFromShared = () => {
        if (name === FilterStoreName.SharedFilter) return;
        const shared = useSharedFilterStore();
        all.value = shared.all ? (structuredClone(toRaw(shared.all)) as EchoFilter) : null;
        onlyResolvedBothCheckedAsNull.value = false;
        selectedFavoriteFilter.value = null;
        selectedFavoriteFilterRequestData.value = null;
        levelFilterWasReset.value = false;
    };

    return {
        all,
        onlyResolvedBothCheckedAsNull,
        selected,
        requestData,
        favoriteComparisonKey,
        recordingStartLabel,
        recordingEndLabel,
        selectedFavoriteFilter,
        selectedFavoriteFilterRequestData,
        filterStoreName,
        loading,
        levelFilterWasReset,
        removeInvalidLevelFilter,
        setSelectedFavoriteFilter,
        clearSelectedFavoriteFilter,
        getFilter,
        applyFilterRequest,
        resetFilter,
        setFilter,
        loadFilter,
        seedFromShared,
        setIncludedWords,
        setExcludedWords,
        setStartEndTime,
        setRelativeDateRange,
        clearAbsoluteDates,
        updateFilter,
        abortPendingRequests: () => {
            getFilterAbortController?.abort();
            applyFilterAbortController?.abort();
            updateFilterAbortController?.abort();
        },
    };
};

type PersistedFilterStoreDef = ReturnType<
    typeof defineStore<FilterStoreName, ReturnType<typeof filterStore>>
>;

const persistedFilterStores = new Map<FilterStoreName, PersistedFilterStoreDef>();

const usePersistedFilterStore = (name: FilterStoreName, pick: string[]) => {
    if (!persistedFilterStores.has(name)) {
        const keycloak = useKeycloak();

        const userUniqueKey = keycloak.tokenParsed?.sub ?? keycloak.subject;
        if (!userUniqueKey) {
            throw new Error(`Cannot initialize ${name} store: no authenticated user ID available`);
        }

        persistedFilterStores.set(
            name,
            defineStore(name, () => filterStore(name), {
                persist: {
                    // v2: a level tree persisted before nodes carried groupId/agentId would be sent as groupIds:[null].
                    key: `${name}-v2-${userUniqueKey}`,
                    storage: sessionStorage,
                    pick,
                },
            })
        );
    }

    return persistedFilterStores.get(name)!();
};

export function useSharedFilterStore() {
    return usePersistedFilterStore(FilterStoreName.SharedFilter, [
        'all',
        'selectedFavoriteFilter',
        'selectedFavoriteFilterRequestData',
    ]);
}

export const useAskMeFilterStore = defineStore(FilterStoreName.AskMeFilter, () =>
    filterStore(FilterStoreName.AskMeFilter)
);

export const useWidgetFilterStore = defineStore(FilterStoreName.WidgetFilter, () =>
    filterStore(FilterStoreName.WidgetFilter)
);

export const useScheduleCoachingFilterStore = defineStore(
    FilterStoreName.ScheduleCoachingFilter,
    () => filterStore(FilterStoreName.ScheduleCoachingFilter)
);

export const useFilterStoreByName = (name: FilterStoreName) => {
    if (name === FilterStoreName.AskMeFilter) {
        return useAskMeFilterStore();
    } else if (name === FilterStoreName.WidgetFilter) {
        return useWidgetFilterStore();
    } else if (name === FilterStoreName.ScheduleCoachingFilter) {
        return useScheduleCoachingFilterStore();
    } else {
        return useSharedFilterStore();
    }
};
